/**
 * テロップ内の部分強調 (リッチテキスト) の正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.2-I。
 *
 * 同じ意味・同じ規則の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出しの注記)
 * - video-editor リポジトリの src/schema/captionEmphasis.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/caption_extensions.py (AI 提案の正規化・合成フレーム)
 *
 * ## 形 (2 つに分かれる)
 *
 * - `Caption.emphasisRanges`: `text` の**どこを**強調するか。`[{start, end}]` の半開区間で、
 *   単位は **コードポイント** (サロゲートペア・絵文字を割らない)。重なりなし・昇順に正規化する。
 *   `text` に依存する属性なので、`text` が変わったら破棄する (§0-3 の依存規則)
 * - `CaptionDesign.emphasis`: **どう**強調するか (`{color?, sizeScale?, bold?}`)。テロップ単位の
 *   スタイルで、そのテロップの強調範囲すべてに同じものが効く (範囲ごとに変えることはできない)
 *
 * ## 記法 (`**強調**`)
 *
 * **保存形の `text` はプレーン文字列**で、記法は保存しない。text の消費者 (テロップ音声同期・
 * XML / SRT 書き出し・原稿との一字一句照合・要約) をどれも壊さないため。
 * 記法は「AI と UI が範囲を書くための入力表現」で、読み書きのたびに
 * `parseEmphasisMarkup` (記法 → プレーン text + ranges) と
 * `toEmphasisMarkup` (プレーン text + ranges → 記法) で往復する。
 *
 * 解析規則:
 * - `**` から次の `**` までを 1 つの強調範囲にする (非入れ子・最短一致)。改行をまたいでもよい
 * - 対応する閉じが無い `**`、中身が空の `****` は**文字としてそのまま残す** (範囲にしない)
 * - 走査はコードポイント単位なので、絵文字・サロゲートペアの途中で切れることはない
 *
 * **既知の制限**: 文言そのものに `**` を含めることはできない (次に開いたときに記法として
 * 解釈される)。日本語のテロップ文言では実害が無いと判断して受け入れている。
 *
 * ## 描画
 *
 * `splitEmphasisLines` が各行を「通常 / 強調」のランに分ける。テロップは縁取り・影・本体の各層で
 * 同じ文字列を重ねて描いているので、**すべての層を同じランで分割**すること
 * (層ごとに分け方が変われば輪郭がずれる)。強調ランには色・フォントサイズ (× `sizeScale`)・
 * 太さ (`bold`) が効く。
 *
 * 描画の実装は 4 か所 (どれかを直したら 4 つとも直す):
 * 1. プレビュー `remotion/EditVideo.tsx` の DesignedCaption (横書き)
 * 2. プレビュー `remotion/VerticalCaption.tsx` (縦書き)
 * 3. MP4 レンダラー (video-editor リポジトリの同名ファイル)
 * 4. 批評・ディレクターが見る合成フレーム `backend/services/video_edit/contact_sheet.py`
 *
 * **5 つ目の「休眠中」の実装**: `export/captionRaster.ts` (Premiere 用の透過 PNG)。横書きは
 * canvas 2D に CSS を写した独自実装、縦書きは VerticalCaption を流用している。**部分強調は
 * 書き出しに含めない方針 (§0-2) なので、ここには意図的にランを渡していない** (プレーンな text
 * だけを描く)。将来 XML 書き出しに強調を載せるなら、この 2 経路にもラン分割が要る。
 *
 * ## 書き出し
 *
 * Premiere の .prtextstyle / FCPXML の title はテロップ単位のスタイルしか持てないので、text は
 * プレーンのまま出し、ZIP の README とトーストで「テロップ内の部分強調は含まれません」と注記する
 * (§0-2。motion・アニメーションと同じ扱い)。
 */

/** 強調範囲 (text 内のコードポイント単位の半開区間 [start, end))。 */
export interface CaptionEmphasisRange {
  start: number;
  end: number;
}

/** 強調のスタイル (テロップ単位。CaptionDesign.emphasis)。 */
export interface CaptionEmphasisStyle {
  /** 強調ランの文字色 (#RRGGBB)。省略 = CAPTION_EMPHASIS_DEFAULT_COLOR。 */
  color?: string;
  /** 強調ランのフォントサイズ倍率。省略 = CAPTION_EMPHASIS_SIZE_SCALE_DEFAULT。 */
  sizeScale?: number;
  /** 強調ランを太くするか。省略 = CAPTION_EMPHASIS_BOLD_DEFAULT。 */
  bold?: boolean;
}

/** 解決済みの強調スタイル (描画側はこれだけを見る)。 */
export interface ResolvedCaptionEmphasisStyle {
  color: string;
  sizeScale: number;
  bold: boolean;
}

/** 行を「通常 / 強調」に分けた 1 区間。 */
export interface CaptionEmphasisRun {
  text: string;
  emphasized: boolean;
  /**
   * 単語ハイライト (§4.2-B2) が掛かるラン。掛からないランではキーごと無い
   * (ハイライトを使わないテロップのランを従来と同じ形に保つため)。
   */
  highlighted?: boolean;
}

/** 記法のマーカー。 */
export const CAPTION_EMPHASIS_MARKER = '**';

/** 強調色の既定 (目立つ黄色系。トーンを選ばず映像から浮きやすい 1 色に固定する)。 */
export const CAPTION_EMPHASIS_DEFAULT_COLOR = '#FFE14D';

/** sizeScale の範囲・刻み・既定値。 */
export const CAPTION_EMPHASIS_SIZE_SCALE_MIN = 0.5;
export const CAPTION_EMPHASIS_SIZE_SCALE_MAX = 1.6;
export const CAPTION_EMPHASIS_SIZE_SCALE_DEFAULT = 1.2;
export const CAPTION_EMPHASIS_SIZE_SCALE_STEP = 0.01;

/** bold の既定 (強調は太くするのが基本)。 */
export const CAPTION_EMPHASIS_BOLD_DEFAULT = true;

/**
 * bold のときに足すウェイト。フォントが提供するウェイトへ丸めるのは emphasisFontWeight
 * (フォントごとの提供ウェイト一覧は呼び出し側が渡す。ここは数式だけを持つ)。
 */
export const CAPTION_EMPHASIS_BOLD_WEIGHT_DELTA = 200;

/** フォントウェイトの上限 (CSS の 100〜900)。 */
const MAX_FONT_WEIGHT = 900;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** half-up の丸め (captionCatalog の roundToStep / caption_extensions の _round_to_step と同じ式)。 */
function roundToStep(value: number, step: number): number {
  const inv = 1 / step;
  return Math.floor(value * inv + 0.5) / inv;
}

/** コードポイント配列 (サロゲートペア・結合文字を割らない)。 */
function toChars(text: string): string[] {
  return Array.from(text);
}

/**
 * 強調範囲を保存形へ正規化する。
 * - 有限の整数でない / 空 (start >= end) / text の外にはみ出た区間は落とす
 * - text 長にクランプし、開始順に並べ、**重なり・隣接はまとめる** (同じ見た目になるため)
 *
 * 有効な区間が 1 つも無ければ `undefined` (キーごと落とす = 保存値は実効値)。
 */
export function normalizeEmphasisRanges(
  ranges: unknown,
  textLength: number,
): CaptionEmphasisRange[] | undefined {
  if (!Array.isArray(ranges) || textLength <= 0) return undefined;
  const cleaned: CaptionEmphasisRange[] = [];
  for (const raw of ranges) {
    if (!raw || typeof raw !== 'object') continue;
    const start = (raw as CaptionEmphasisRange).start;
    const end = (raw as CaptionEmphasisRange).end;
    if (!isFiniteNumber(start) || !isFiniteNumber(end)) continue;
    const s = clamp(Math.floor(start), 0, textLength);
    const e = clamp(Math.floor(end), 0, textLength);
    if (s >= e) continue;
    cleaned.push({ start: s, end: e });
  }
  if (cleaned.length === 0) return undefined;
  cleaned.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: CaptionEmphasisRange[] = [];
  for (const range of cleaned) {
    const last = merged[merged.length - 1];
    // 重なり (start < last.end) も隣接 (start === last.end) もまとめる
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end);
      continue;
    }
    merged.push({ ...range });
  }
  return merged;
}

/** 範囲を持つか (要約・書き出しの注記で使う)。 */
export function hasCaptionEmphasisRanges(
  caption: { text?: string; emphasisRanges?: CaptionEmphasisRange[] | null } | null | undefined,
): boolean {
  if (!caption) return false;
  return (normalizeEmphasisRanges(caption.emphasisRanges, toChars(caption.text ?? '').length)?.length ?? 0) > 0;
}

/**
 * 記法 (`**強調**`) を解析して、プレーン text と強調範囲に分ける。
 * 対応する閉じが無い `**` と空の `****` は文字としてそのまま残す。
 */
export function parseEmphasisMarkup(raw: string): { text: string; ranges: CaptionEmphasisRange[] } {
  const chars = toChars(typeof raw === 'string' ? raw : '');
  const out: string[] = [];
  const ranges: CaptionEmphasisRange[] = [];
  let i = 0;
  while (i < chars.length) {
    if (chars[i] === '*' && chars[i + 1] === '*') {
      // 次の `**` を探す (非入れ子・最短一致)
      let close = -1;
      for (let j = i + 2; j + 1 < chars.length; j += 1) {
        if (chars[j] === '*' && chars[j + 1] === '*') {
          close = j;
          break;
        }
      }
      if (close > i + 2) {
        const start = out.length;
        for (let k = i + 2; k < close; k += 1) out.push(chars[k] as string);
        ranges.push({ start, end: out.length });
        i = close + 2;
        continue;
      }
      // 閉じが無い / 中身が空 → 文字として残す
      out.push('*', '*');
      i += 2;
      continue;
    }
    out.push(chars[i] as string);
    i += 1;
  }
  const text = out.join('');
  return { text, ranges: normalizeEmphasisRanges(ranges, out.length) ?? [] };
}

/** プレーン text + 強調範囲を記法付きの文字列に戻す (UI の文言エディタが表示・編集する形)。 */
export function toEmphasisMarkup(
  text: string,
  ranges: CaptionEmphasisRange[] | null | undefined,
): string {
  const chars = toChars(typeof text === 'string' ? text : '');
  const normalized = normalizeEmphasisRanges(ranges, chars.length);
  if (!normalized || normalized.length === 0) return chars.join('');
  const out: string[] = [];
  let pos = 0;
  for (const range of normalized) {
    out.push(chars.slice(pos, range.start).join(''));
    out.push(CAPTION_EMPHASIS_MARKER, chars.slice(range.start, range.end).join(''), CAPTION_EMPHASIS_MARKER);
    pos = range.end;
  }
  out.push(chars.slice(pos).join(''));
  return out.join('');
}

/**
 * text を「通常 / 強調」のランに分ける (全文。改行はランの中に残る)。
 *
 * `highlightEnd` (§4.2-B2 の単語ハイライト) を渡すと、**先頭から highlightEnd 文字目**でもランを
 * 割り、そこまでのランに `highlighted: true` を付ける (強調と重なるランは両方が立つ)。
 * 省略 / 0 のときは従来どおり強調だけで割るので、ハイライトを使わないテロップの描画は変わらない。
 */
export function splitEmphasisRuns(
  text: string,
  ranges: CaptionEmphasisRange[] | null | undefined,
  highlightEnd?: number | null,
): CaptionEmphasisRun[] {
  const chars = toChars(typeof text === 'string' ? text : '');
  const normalized = normalizeEmphasisRanges(ranges, chars.length) ?? [];
  const highlight = isFiniteNumber(highlightEnd) ? clamp(Math.floor(highlightEnd), 0, chars.length) : 0;

  // 境界点 (強調の両端 + ハイライトの終端) で区切る
  const bounds = new Set<number>([0, chars.length]);
  for (const range of normalized) {
    bounds.add(range.start);
    bounds.add(range.end);
  }
  if (highlight > 0) bounds.add(highlight);
  const points = Array.from(bounds).sort((a, b) => a - b);

  const runs: CaptionEmphasisRun[] = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const start = points[i] as number;
    const end = points[i + 1] as number;
    if (start >= end) continue;
    const emphasized = normalized.some((range) => range.start <= start && end <= range.end);
    const run: CaptionEmphasisRun = { text: chars.slice(start, end).join(''), emphasized };
    if (highlight > 0 && end <= highlight) run.highlighted = true;
    runs.push(run);
  }
  if (runs.length === 0) runs.push({ text: '', emphasized: false });
  return runs;
}

/**
 * text を行ごと (改行区切り)・ラン単位に分ける。描画側 (横書き・縦書き・合成フレーム) は
 * これを使って 1 行ずつ左から順にランを描く。空行は空配列になる。
 * `highlightEnd` は splitEmphasisRuns と同じ (§4.2-B2)。
 */
export function splitEmphasisLines(
  text: string,
  ranges: CaptionEmphasisRange[] | null | undefined,
  highlightEnd?: number | null,
): CaptionEmphasisRun[][] {
  const lines: CaptionEmphasisRun[][] = [[]];
  for (const run of splitEmphasisRuns(text, ranges, highlightEnd)) {
    const parts = run.text.split('\n');
    parts.forEach((part, index) => {
      if (index > 0) lines.push([]);
      if (part) {
        const next: CaptionEmphasisRun = { text: part, emphasized: run.emphasized };
        if (run.highlighted) next.highlighted = true;
        (lines[lines.length - 1] as CaptionEmphasisRun[]).push(next);
      }
    });
  }
  return lines;
}

/**
 * タイプライター (§4.2-B1) で先頭 n 文字だけ描くときに、範囲も同じところで切る。
 * `maxLength` はコードポイント数。
 */
export function clipEmphasisRanges(
  ranges: CaptionEmphasisRange[] | null | undefined,
  maxLength: number,
): CaptionEmphasisRange[] {
  return normalizeEmphasisRanges(ranges, Math.max(0, Math.floor(maxLength))) ?? [];
}

/**
 * CaptionDesign.emphasis の外部入力 (AI の提案・古い保存データ) を保存形へ正規化する。
 * **保存値 = 実効値**にするため、既定値と同じ項目・不正値はキーごと落とし、
 * 何も残らなければ `undefined` (emphasis キー自体を落とす)。
 */
export function normalizeCaptionEmphasisStyle(value: unknown): CaptionEmphasisStyle | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as CaptionEmphasisStyle;
  const result: CaptionEmphasisStyle = {};
  if (typeof raw.color === 'string' && /^#[0-9a-fA-F]{6}$/.test(raw.color)) {
    result.color = raw.color.toUpperCase();
  }
  if (isFiniteNumber(raw.sizeScale)) {
    const scale = roundToStep(
      clamp(raw.sizeScale, CAPTION_EMPHASIS_SIZE_SCALE_MIN, CAPTION_EMPHASIS_SIZE_SCALE_MAX),
      CAPTION_EMPHASIS_SIZE_SCALE_STEP,
    );
    // 省略時の既定と区別するため、等倍も保持する。
    result.sizeScale = scale; // 等倍も明示値として保持 (省略時の既定は1.2倍)。
  }
  if (typeof raw.bold === 'boolean' && raw.bold !== CAPTION_EMPHASIS_BOLD_DEFAULT) {
    result.bold = raw.bold;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

/** 強調スタイルの実効値 (描画側はこれだけを見る)。 */
export function resolveCaptionEmphasisStyle(
  design: { emphasis?: CaptionEmphasisStyle | null } | null | undefined,
): ResolvedCaptionEmphasisStyle {
  const emphasis = normalizeCaptionEmphasisStyle(design?.emphasis);
  return {
    color: emphasis?.color ?? CAPTION_EMPHASIS_DEFAULT_COLOR,
    sizeScale: emphasis?.sizeScale ?? CAPTION_EMPHASIS_SIZE_SCALE_DEFAULT,
    bold: emphasis?.bold ?? CAPTION_EMPHASIS_BOLD_DEFAULT,
  };
}

/**
 * 強調ランに使うフォントウェイト。フォントが提供するウェイト (`availableWeights`) の中から、
 * `baseWeight + CAPTION_EMPHASIS_BOLD_WEIGHT_DELTA` 以上で最も近いものを選ぶ
 * (無ければ提供されている最大値)。base より太くならないときは base をそのまま返す
 * (ブラウザーの擬似ボールドに頼ると 3 実装で見た目がずれるため、実在するウェイトだけを使う)。
 */
export function emphasisFontWeight(
  baseWeight: number,
  availableWeights: readonly number[] | undefined,
): number {
  const base = isFiniteNumber(baseWeight) ? baseWeight : 400;
  const weights = (availableWeights ?? []).filter(isFiniteNumber).slice().sort((a, b) => a - b);
  if (weights.length === 0) return base;
  const target = Math.min(MAX_FONT_WEIGHT, base + CAPTION_EMPHASIS_BOLD_WEIGHT_DELTA);
  const picked = weights.find((w) => w >= target) ?? (weights[weights.length - 1] as number);
  return picked > base ? picked : base;
}

/** UI / 要約の表示名 (「強調 #FFE14D ×1.2 太字」)。強調範囲が無いテロップでは呼ばない。 */
export function captionEmphasisLabel(
  design: { emphasis?: CaptionEmphasisStyle | null } | null | undefined,
): string {
  const style = resolveCaptionEmphasisStyle(design);
  const parts = [style.color, `×${style.sizeScale}`];
  if (style.bold) parts.push('太字');
  return parts.join(' ');
}
